<section
  class="reference-properties"
  aria-label="Selected reference properties"
>
  <h4>Protected reference</h4>
  @if (validationError) {
    <p class="reference-warning" role="alert">{{ validationError }}</p>
  }
  @if (current; as run) {
    <p class="reference-identity">
      {{ run.kind === 'field' ? 'Data field' : 'Terminology' }} ·
      <code>{{ run.value || 'Empty reference' }}</code>
    </p>
    <label>
      Binding
      <select
        uiControl
        name="reference-binding"
        aria-label="Reference binding"
        [disabled]="disabled || !choices.length"
        [ngModel]="run.value"
        (ngModelChange)="changeBinding($event)"
      >
        @if (!knownBinding) {
          <option [value]="run.value" data-unavailable="binding">
            Unavailable: {{ run.value || 'Empty reference' }}
          </option>
        }
        @for (choice of choices; track choice.code) {
          <option [value]="choice.code">
            {{ choice.label }} · {{ choice.code }}
          </option>
        }
      </select>
    </label>
    @if (!knownBinding) {
      <p class="reference-warning">
        This retained binding is unavailable in the current contract. Its
        identity is preserved until you choose a registered binding.
      </p>
    }
    @if (field; as definition) {
      <dl class="reference-metadata">
        <dt>Type</dt>
        <dd>{{ definition.dataType }}</dd>
        <dt>Classification</dt>
        <dd>{{ definition.classification }}</dd>
        <dt>Value</dt>
        <dd>{{ definition.required ? 'Required' : 'Optional' }}</dd>
      </dl>
      <p class="reference-guidance">
        {{ missingBehaviorLabel(definition.missingBehavior) }}
      </p>
    }
    @if (formats.length) {
      <label>
        Format
        <select
          uiControl
          name="reference-format"
          aria-label="Reference format"
          [disabled]="disabled"
          [ngModel]="formatValue"
          (ngModelChange)="changeFormat($event)"
        >
          @if (!knownFormat) {
            <option [ngValue]="formatValue" data-unavailable="format">
              Unavailable: {{ run.format || 'Empty format' }}
            </option>
          }
          <option [ngValue]="null">Default ({{ formats[0] }})</option>
          @for (format of formats; track format) {
            <option [ngValue]="format">{{ format }}</option>
          }
        </select>
      </label>
    } @else if (run.format !== undefined) {
      <p class="reference-warning" data-unavailable="format">
        Unavailable format: {{ run.format || 'Empty format' }}.
        <button
          uiButton="secondary"
          type="button"
          [disabled]="disabled"
          (click)="changeFormat(null)"
        >
          Remove unsupported format
        </button>
      </p>
    }
    @if (term; as definition) {
      <dl class="reference-metadata">
        <dt>Token</dt>
        <dd>{{ definition.tokenCode }}</dd>
        <dt>Configuration</dt>
        <dd>{{ definition.configurationCode }}</dd>
        <dt>Declared fallback</dt>
        <dd>
          {{ capturedNode?.registeredFallbackText ?? definition.fallbackText }}
        </dd>
      </dl>
    }
    <section class="reference-wording" aria-label="Exact reference wording">
      <h5>Rendered wording</h5>
      @if (exactWordingAvailable) {
        @if (resolvedText === '') {
          <p class="reference-guidance">Empty value in this synthetic proof.</p>
        } @else {
          <p class="resolved-wording">{{ resolvedText }}</p>
        }
        <p class="reference-guidance">{{ proofLabel }}</p>
        @if (capturedNode; as node) {
          <dl
            class="reference-metadata"
            aria-label="Captured terminology identity"
          >
            <dt>Definition</dt>
            <dd>{{ node.definitionKindCode }}</dd>
            @if (node.familyCode) {
              <dt>Family</dt>
              <dd>{{ node.familyCode }}</dd>
            }
            @if (node.grammaticalFormCode) {
              <dt>Form</dt>
              <dd>{{ node.grammaticalFormCode }}</dd>
            }
            @if (node.publicationId) {
              <dt>Publication / revision</dt>
              <dd>{{ node.publicationId }} / {{ node.revisionId }}</dd>
            }
          </dl>
        }
        @if (capturedTerm; as evidence) {
          <dl
            class="reference-metadata"
            aria-label="Captured terminology resolution"
          >
            <dt>Resolution</dt>
            <dd>
              {{
                evidence.usedFallback
                  ? 'Declared fallback used'
                  : 'Resolved wording'
              }}
            </dd>
            <dt>Reason</dt>
            <dd>{{ evidence.reasonCode }}</dd>
            @if (evidence.configurationVersionCode) {
              <dt>Version</dt>
              <dd>{{ evidence.configurationVersionCode }}</dd>
            } @else if (evidence.configurationVersionId !== null) {
              <dt>Version ID</dt>
              <dd>{{ evidence.configurationVersionId }}</dd>
            }
            @if (evidence.matchedScopeTypeCode) {
              <dt>Matched scope</dt>
              <dd>
                {{ evidence.matchedScopeTypeCode
                }}{{
                  evidence.matchedScopeKey
                    ? ' · ' + evidence.matchedScopeKey
                    : ''
                }}
              </dd>
            }
          </dl>
        }
      } @else {
        <p class="reference-guidance">
          Preview required. Save and preview the selected synthetic example to
          inspect this reference's exact wording.
        </p>
      }
    </section>
    <button
      uiButton="secondary"
      type="button"
      [disabled]="disabled"
      (click)="returnFocus()"
    >
      Return to text
    </button>
  } @else {
    <p class="reference-guidance">
      Select a protected reference on the page or from the reference list.
    </p>
  }
</section>
